import React, { useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { MultiStatePrivacyStyles } from './MultiStatePrivacy.styled';
import { tablet_portrait_breakpoint } from 'pubweb-smokey/dist/components/GridSystem/_vars_widths';
import useWindowResize from 'pubweb-smokey/dist/hooks/useWindowResize';

const MultiStatePrivacy = () => {
  const [isResponsive, setIsResponsive] = useState(false);

  useWindowResize(() => {
    window.innerWidth < tablet_portrait_breakpoint
      ? setIsResponsive(true)
      : setIsResponsive(false);
  }, [tablet_portrait_breakpoint]);

  const webFormLink = (
    <Link href="/privacy/data-privacy-request-form">web form</Link>
  );
  const financialLink = (
    <Link href="/privacy/financial-institution-privacy-policy">
      Financial Privacy Notice
    </Link>
  );
  const empCaNoticeCollectionLink = (
    <Link href="/privacy/clayton-employee-ca-notice-collection-privacy-policy">
      CA Employee Notice at Collection and Privacy Policy
    </Link>
  );
  const checkedBox = (
    <p>
      <span>
        <img
          width="18"
          src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAADSSURBVEhL7ZRBDsIwDAQj/gCCHwE/gefCgTvlEdxgV9SScewSB7h1pFXaKOuNErdlJsseGqBHUndoB5EVdHg91vyi+Bni/JETFjH1sIROEP0XaA1V9AY0FSc9Ac3FSTYgVZxkAvSFcuQ7kQt3sQFsNzFqpoqzq0J0ANvMFiBRccLi4nfRAfZ8N86cPXPtd7EL7G6jnQvWX+Et0LumprrF878RLZCQT63YHUB4JN6xaL4KaKHyL8bxb9iA2zjKTrIi/OWH8Eu8Qp65RfRuoZlWSnkC6DOQOhvK5p0AAAAASUVORK5CYII="
          alt="image"
        />
      </span>
    </p>
  );

  return (
    <MultiStatePrivacyStyles>
      <Head>
        <title>Multi State Privacy Policy | Clayton Homes</title>
        <meta
          name="description"
          content="We respect your privacy and are committed to providing a transparent notice of our Privacy Notice."
        />
      </Head>
      <div className="multi-state-privacy-content">
        <h1 className="page-heading">
          Multi-State Notice at Collection and Privacy Policy
        </h1>
        <p>
          <b>Effective Date:</b> January 1, 2023
        </p>
        <p>
          <b>Last Revised/Reviewed on:</b> October 10, 2024
        </p>
        <p>
          <span>
            <br />
            This Multi-State Notice at Collection and Privacy Policy
            (&quot;State Privacy Disclosure&quot;) explains how Clayton Homes,
            Inc. and its subsidiaries and affiliates (&quot;Clayton&quot;,
            &quot;Company&quot;, &quot;us&quot;, &quot;our&quot;, or
            &quot;we&quot;) collect, use, disclose, sell, share, and/or retain
            personal information (&quot;Information Practices&quot;) subject to
            applicable U.S. state privacy laws.
          </span>
        </p>

        <p>
          <span>
            Certain states provide specific rights to their residents as listed
            below. While we will attempt to respond to a request from any U.S.
            resident, we reserve the right to deny requests if allowed under
            applicable law. Unless you are a California resident, any rights
            that may be available in your state are not afforded to you under
            this State Privacy Disclosure while you are operating in your
            commercial or employment capacity.
          </span>
        </p>

        <p>
          <span>
            The terms used in this State Privacy Disclosure shall have the same
            or similar meaning as those terms in each of the applicable
            state&apos;s laws, or the closest approximation of such terms.
          </span>
        </p>

        <p>
          <span>
            If you are a consumer or customer of personal, family, or household
            financial products or services from us, please review our&nbsp;
          </span>
          {financialLink}
          <span>.</span>
        </p>

        <p>
          <span>
            If you are a California resident who has interacted with us in an
            employment or job applicant capacity, please review our&nbsp;
          </span>
          {empCaNoticeCollectionLink}
          <span>.</span>
        </p>

        <div className="divider-line" />

        <p className="centered-header">NOTICE AT COLLECTION</p>
        <p>
          <span>
            <br />
            The following notice identifies the categories of personal
            information to be collected from you and the purposes for which the
            personal information will be used, including whether that
            information is sold or shared for the purposes of cross-context
            behavioral advertising (<em>e.g.</em>, targeted advertising).
          </span>
        </p>

        <p className="centered-header">General Personal Information</p>
        <div className="responsive-table">
          <table>
            <tbody>
              <tr>
                <th>
                  <p className="table-1-col-1-heading">
                    <strong>
                      <span>Categories of Personal Information</span>
                    </strong>
                  </p>
                </th>
                <th>
                  <p className="table-1-col-2-heading">
                    <strong>
                      <span>Purposes for Collection, Use, or Processing</span>
                    </strong>
                  </p>
                </th>
                <th>
                  <p className="table-1-col-3-heading">
                    <strong>
                      <span>
                        Sold to Third Parties or Shared with Third Parties for
                        Cross-Context Behavioral Advertising
                      </span>
                    </strong>
                  </p>
                </th>
              </tr>
              <tr>
                <td className="table-1-col-1-content">
                  <p>
                    <strong>
                      <span>Identifiers</span>
                    </strong>
                    <span>
                      : For example, a name, address, online identifier,
                      Internet Protocol (&quot;IP&quot;) address, email address,
                      or other similar identifiers.
                    </span>
                  </p>
                </td>
                {!isResponsive ? noticeCollectionGeneralInfo() : null}
                <td className="table-1-col-3-content">
                  {isResponsive ? table1Col3Heading() : null}
                  <p>
                    <span>
                      We sell/share IP address, device type and characteristics,
                      unique device identifiers to web analytics providers and
                      advertising partners
                    </span>
                  </p>
                </td>
              </tr>
              <tr>
                <td className="table-1-col-1-content">
                  <p>
                    <strong>
                      <span>(Pursuant to California Law)</span>
                    </strong>
                  </p>
                  <p>
                    <strong>
                      <span>
                        Personal Information Categories from Cal. Civ. Code
                        &sect; 1798.80(e):&nbsp;
                      </span>
                    </strong>
                    <span>
                      For example, a name, social security number, address,
                      telephone number, credit card number, or debit card
                      number.
                    </span>
                  </p>
                </td>
                <td className="table-1-col-3-content">
                  {isResponsive ? table1Col3Heading() : null}
                  <p>
                    <span>No</span>
                  </p>
                </td>
              </tr>
              <tr>
                <td className="table-1-col-1-content">
                  <p>
                    <strong>
                      <span>(Pursuant to California Law)</span>
                    </strong>
                  </p>
                  <p>
                    <strong>
                      <span>
                        Characteristics of CA or Federal Protected
                        Classifications:&nbsp;
                      </span>
                    </strong>
                    <span>
                      For example, race, religion, age (40 and over), gender, or
                      sexual orientation.
                    </span>
                  </p>
                </td>
                <td className="table-1-col-3-content">
                  {isResponsive ? table1Col3Heading() : null}
                  <p>
                    <span>No</span>
                  </p>
                </td>
              </tr>
              <tr>
                <td className="table-1-col-1-content">
                  <p>
                    <strong>
                      <span>Commercial Information:&nbsp;</span>
                    </strong>
                    <span>
                      For example, records of personal property, products or
                      services purchased, obtained, or considered, or other
                      purchasing or consuming histories or tendencies.
                    </span>
                  </p>
                </td>
                <td className="table-1-col-3-content">
                  {isResponsive ? table1Col3Heading() : null}
                  <p>
                    <span>No</span>
                  </p>
                </td>
              </tr>
              <tr>
                <td className="table-1-col-1-content">
                  <p>
                    <strong>
                      <span>Biometric Information:&nbsp;</span>
                    </strong>
                    <span>
                      For example, physiological, biological or behavioral
                      characteristics, that can be used to establish individual
                      identity.
                    </span>
                  </p>
                </td>
                <td className="table-1-col-3-content">
                  {isResponsive ? table1Col3Heading() : null}
                  <p>
                    <span>No</span>
                  </p>
                </td>
              </tr>
              <tr>
                <td className="table-1-col-1-content">
                  <p>
                    <strong>
                      <span>Internet or Other Similar Network Activity:</span>
                    </strong>
                    <span>
                      &nbsp;For example, browsing history, search history, and
                      information regarding a consumer&apos;s interaction with
                      an Internet Web site, application, or advertisement.
                    </span>
                  </p>
                </td>
                <td className="table-1-col-3-content">
                  {isResponsive ? table1Col3Heading() : null}
                  <p>
                    <span>
                      We sell/share to web analytics providers and advertising
                      partners
                    </span>
                  </p>
                </td>
              </tr>
              <tr>
                <td className="table-1-col-1-content">
                  <p>
                    <strong>
                      <span>Geolocation Data:</span>
                    </strong>
                    <span>
                      &nbsp;For example, information that can be used to
                      determine a device&apos;s physical location.
                    </span>
                  </p>
                </td>
                <td className="table-1-col-3-content">
                  {isResponsive ? table1Col3Heading() : null}
                  <p>
                    <span>
                      We sell/share to web analytics providers and advertising
                      partners
                    </span>
                  </p>
                </td>
              </tr>
              <tr>
                <td className="table-1-col-1-content">
                  <p>
                    <strong>
                      <span>Sensory or Surveillance Data:&nbsp;</span>
                    </strong>
                    <span>
                      For example, call recordings or other audio, electronic,
                      visual monitoring or surveillance.
                    </span>
                  </p>
                </td>
                <td className="table-1-col-3-content">
                  {isResponsive ? table1Col3Heading() : null}
                  <p>
                    <span>No</span>
                  </p>
                </td>
              </tr>
              <tr>
                <td className="table-1-col-1-content">
                  <p>
                    <strong>
                      <span>Profile Data</span>
                    </strong>
                    <span>
                      : For example, inferences drawn from personal information
                      to create a profile about a consumer reflecting the
                      consumer&rsquo;s preferences, characteristics,
                      predispositions, behavior, attitudes, abilities, and
                      aptitudes.
                    </span>
                  </p>
                </td>
                <td className="table-1-col-3-content">
                  {isResponsive ? table1Col3Heading() : null}
                  <p>
                    <span>No</span>
                  </p>
                </td>
                {isResponsive ? noticeCollectionGeneralInfo() : null}
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          <strong>
            <u>
              <span>We do not collect</span>
            </u>
          </strong>
          <span>
            : professional or employment related information or education
            information outside of an individual acting in their commercial or
            employment-related context or under this policy.
          </span>
        </p>

        <p className="centered-header">Sensitive Personal Information</p>
        <div className="responsive-table">
          <table>
            <tbody>
              <tr>
                <th>
                  <p className="table-2-col-1-heading">
                    <strong>
                      <span>Categories of Sensitive Personal Information</span>
                    </strong>
                  </p>
                </th>
                <th>
                  <p className="table-2-col-2-heading">
                    <strong>
                      <span>Purposes for Collection, Use, or Processing</span>
                    </strong>
                  </p>
                </th>
                <th>
                  <p className="table-2-col-3-heading">
                    <strong>
                      <span>
                        Sold to or Shared with Third Parties for Cross-Context
                        Behavioral Advertising
                      </span>
                    </strong>
                  </p>
                </th>
              </tr>
              <tr>
                <td className="table-2-col-1-content">
                  <p>
                    <span>
                      Social Security Number, Driver&apos;s License, State
                      Identification Card, or Passport Number
                    </span>
                  </p>
                </td>
                {!isResponsive ? noticeCollectionSensitiveInfo() : null}
                <td className="table-2-col-3-content">
                  {isResponsive ? table2Col3Heading() : null}
                  <p>
                    <span>No</span>
                  </p>
                </td>
              </tr>
              <tr>
                <td className="table-2-col-1-content">
                  <p>
                    <span>
                      Account log-in, financial account, debit card, or credit
                      card number when provided with any required security or
                      access code, password, or credentials allowing access to
                      an account
                    </span>
                  </p>
                </td>
                <td className="table-2-col-3-content">
                  {isResponsive ? table2Col3Heading() : null}
                  <p>
                    <span>No</span>
                  </p>
                </td>
              </tr>
              <tr>
                <td className="table-2-col-1-content">
                  <p>
                    <span>Precise geolocation</span>
                  </p>
                </td>
                <td className="table-2-col-3-content">
                  {isResponsive ? table2Col3Heading() : null}
                  <p>
                    <span>No</span>
                  </p>
                </td>
              </tr>
              <tr>
                <td className="table-2-col-1-content">
                  <p>
                    <span>
                      Racial or ethnic origin, or religious or philosophical
                      beliefs
                    </span>
                  </p>
                </td>
                <td className="table-2-col-3-content">
                  {isResponsive ? table2Col3Heading() : null}
                  <p>
                    <span>No</span>
                  </p>
                </td>
              </tr>
              <tr>
                <td className="table-2-col-1-content">
                  <p>
                    <span>
                      Biometric data for the purpose of uniquely identifying an
                      individual
                    </span>
                  </p>
                </td>
                <td className="table-2-col-3-content">
                  {isResponsive ? table2Col3Heading() : null}
                  <p>
                    <span>No</span>
                  </p>
                </td>
                {isResponsive ? noticeCollectionSensitiveInfo() : null}
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          <strong>
            <span>
              <br />
              <u>We do not collect:</u>
            </span>
          </strong>
          <span>
            &nbsp;other sensitive personal information under applicable state
            laws.
          </span>
        </p>
        <p className="centered-header">Retention Periods</p>

        <p>
          <span>
            We retain information for different periods of time depending on the
            purposes for which we collect and use it. We will delete or
            de-identify information when it is no longer needed to fulfill the
            purposes listed for each category above, unless a longer retention
            period is required to comply with applicable laws. There may be
            technical or other operational reasons where we are unable to fully
            delete or de-identify your information. Where this is the case, we
            will take reasonable measures to prevent further processing your
            information.
          </span>
        </p>

        <p className="centered-header">Notice of Sale and/or Share</p>
        <p>
          <span>
            Our use of tracking technologies may be considered a sale or sharing
            with third parties for cross-context behavioral advertising (e.g.,
            targeted advertising) under applicable state law. You can opt-out of
            being tracked by these third parties by clicking the &quot;Do Not
            Sell or Share My Personal Information&quot; link and selecting your
            preferences. Please note that this link provides opt-out rights
            under all applicable state laws, also known as &ldquo;opt-out
            rights&rdquo; or &ldquo;personal data use opt-out&rdquo;.
          </span>
        </p>

        <p>
          <span>Please review our Multi-State Privacy Policy below.</span>
        </p>

        <div className="divider-line" />

        <p className="centered-header">PRIVACY POLICY</p>
        <p>
          <span>
            <br />
            The purpose of this privacy policy is to provide you with a
            comprehensive description of our online and offline practices
            regarding our Information Practices. You have certain rights to
            control the use of your personal information. This privacy policy
            describes your rights as it relates to our Information Practices as
            to your personal information and how you can exercise those rights.
          </span>
        </p>

        <p>
          <span>
            The following table includes disclosures for the preceding 12 months
            of: categories of personal information we have collected about
            consumers, the categories we have disclosed for a business purpose,
            and categories of third parties with whom we shared the personal
            information during that period.
          </span>
        </p>

        <p className="centered-header">General Personal Information</p>

        <div className="responsive-table">
          <table>
            <tbody>
              <tr>
                <th rowSpan="2">
                  <p className="category-heading">
                    <strong>
                      <span>Category</span>
                    </strong>
                  </p>
                </th>
                <th colSpan="2">
                  <p className="month-heading">
                    <strong>
                      <span>In the preceding 12 months this category was:</span>
                    </strong>
                  </p>
                </th>
                <th rowSpan="2">
                  <p className="disclosure-heading">
                    <strong>
                      <span>
                        Business or Commercial Purpose for Collection &amp;
                        Disclosure
                      </span>
                    </strong>
                  </p>
                </th>
                <th rowSpan="2">
                  <p className="third-party-heading">
                    <strong>
                      <span>
                        Categories of third parties to whom the information was
                        disclosed
                      </span>
                    </strong>
                  </p>
                </th>
              </tr>
              <tr>
                <th>
                  <p className="collected-heading">
                    <strong>
                      <span>Collected</span>
                    </strong>
                  </p>
                </th>
                <th>
                  <p className="disclosed-heading">
                    <strong>
                      <span>Disclosed to Third Parties</span>
                    </strong>
                  </p>
                </th>
              </tr>
              <tr>
                <td className="category-content">
                  <p>
                    <span>Identifiers</span>
                  </p>
                </td>
                <td className="collected-content">{checkedBox}</td>
                <td className="disclosed-content">{checkedBox}</td>
                {!isResponsive ? privacyPolicyGeneralInfo() : null}
              </tr>
              <tr>
                <td className="category-content">
                  <p>
                    <strong>
                      <span>(Pursuant to California Law)</span>
                    </strong>
                  </p>
                  <p>
                    <span>
                      Personal Information Categories from Cal. Civ. Code &sect;
                      1798.80(e)
                    </span>
                  </p>
                </td>
                <td className="collected-content">{checkedBox}</td>
                <td className="disclosed-content">{checkedBox}</td>
              </tr>
              <tr>
                <td className="category-content">
                  <p>
                    <strong>
                      <span>(Pursuant to California Law)</span>
                    </strong>
                  </p>
                  <p>
                    <span>
                      Characteristics of California or Federal protected
                      Classifications
                    </span>
                  </p>
                </td>
                <td className="collected-content">{checkedBox}</td>
                <td className="disclosed-content">{checkedBox} </td>
              </tr>
              <tr>
                <td className="category-content">
                  <p>
                    <span>Commercial Information</span>
                  </p>
                </td>
                <td className="collected-content">{checkedBox} </td>
                <td className="disclosed-content">{checkedBox} </td>
              </tr>
              <tr>
                <td className="category-content">
                  <p>
                    <span>Biometric Information</span>
                  </p>
                </td>
                <td className="collected-content">{checkedBox} </td>
                <td className="disclosed-content">{checkedBox} </td>
              </tr>
              <tr>
                <td className="category-content">
                  <p>
                    <span>Internet or Other Similar Network Activity</span>
                  </p>
                </td>
                <td className="collected-content">{checkedBox} </td>
                <td className="disclosed-content">{checkedBox} </td>
              </tr>
              <tr>
                <td className="category-content">
                  <p>
                    <span>Geolocation Data</span>
                  </p>
                </td>
                <td className="collected-content">{checkedBox} </td>
                <td className="disclosed-content">{checkedBox} </td>
              </tr>
              <tr>
                <td className="category-content">
                  <p>
                    <span>Sensory or Surveillance Data</span>
                  </p>
                </td>
                <td className="collected-content">{checkedBox} </td>
                <td className="disclosed-content">{checkedBox} </td>
              </tr>
              <tr>
                <td className="category-content">
                  <p>
                    <span>Profile Data</span>
                  </p>
                </td>
                <td className="collected-content">{checkedBox} </td>
                <td className="disclosed-content">{checkedBox} </td>
                {isResponsive ? privacyPolicyGeneralInfo() : null}
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          <strong>
            <u>
              <span>We do not collect</span>
            </u>
          </strong>
          <strong>
            <span>:</span>
          </strong>
          <span>
            &nbsp;professional or employment related information or education
            information&nbsp;
          </span>
          <span>
            outside of an individual acting in their commercial or
            employment-related context
          </span>
          <span>&nbsp;or under this policy</span>
          <span>.</span>
        </p>

        <p className="centered-header">Sensitive Personal Information</p>

        <p>
          <span>
            Unless otherwise allowed by applicable state law, we will only
            collect certain types of sensitive personal information with your
            consent. We do not use or disclose the below sensitive personal
            information for purposes other than those business purposes
            specifically enumerated under applicable state law, including
            California. As a result, we do not offer a right to limit our use or
            disclosure of these categories of sensitive personal information.
          </span>
        </p>

        <div className="responsive-table">
          <table>
            <tbody>
              <tr>
                <th rowSpan="2">
                  <p className="category-heading">
                    <strong>
                      <span>Category</span>
                    </strong>
                  </p>
                </th>
                <th colSpan="2">
                  <p className="month-heading">
                    <strong>
                      <span>In the preceding 12 months this category was:</span>
                    </strong>
                  </p>
                </th>
                <th className="disclosure-heading" rowSpan="2">
                  <p>
                    <strong>
                      <span>
                        Business or Commercial Purpose for Collection &amp;
                        Disclosure
                      </span>
                    </strong>
                  </p>
                </th>
                <th className="third-party-heading" rowSpan="2">
                  <p>
                    <strong>
                      <span>
                        Categories of third parties to whom the information was
                        disclosed
                      </span>
                    </strong>
                  </p>
                </th>
              </tr>
              <tr>
                <th>
                  <p className="collected-heading">
                    <strong>
                      <span>Collected</span>
                    </strong>
                  </p>
                </th>
                <th>
                  <p className="disclosed-heading">
                    <strong>
                      <span>Disclosed to Third Parties</span>
                    </strong>
                  </p>
                </th>
              </tr>
              <tr>
                <td className="category-content">
                  <p>
                    <strong>
                      <span>(Pursuant to California Law)</span>
                    </strong>
                  </p>
                  <p>
                    <span>
                      Social Security Number, Driver&apos;s License, State
                      Identification Card, or Passport Number
                    </span>
                  </p>
                </td>
                <td className="collected-content">{checkedBox} </td>
                <td className="disclosed-content">{checkedBox} </td>
                {!isResponsive ? privacyPolicySensitiveInfo() : null}
              </tr>
              <tr>
                <td className="category-content">
                  <p>
                    <strong>
                      <span>(Pursuant to California Law)</span>
                    </strong>
                  </p>
                  <p>
                    <span>
                      Account log-in, financial account, debit card, or credit
                      card number when provided with any required security or
                      access code, password, or credentials allowing access to
                      an account
                    </span>
                  </p>
                </td>
                <td className="collected-content">{checkedBox} </td>
                <td className="disclosed-content">{checkedBox} </td>
              </tr>
              <tr>
                <td className="category-content">
                  <p>
                    <strong>
                      <span>
                        (Pursuant to California, Delaware, Nebraska, and
                        Virginia Law)
                      </span>
                    </strong>
                  </p>
                  <p>
                    <span>Precise geolocation</span>
                  </p>
                </td>
                <td className="collected-content">{checkedBox} </td>
                <td className="disclosed-content">{checkedBox} </td>
              </tr>
              <tr>
                <td className="category-content">
                  <p>
                    <span>
                      Racial or ethnic origin, or religious or philosophical
                      beliefs
                    </span>
                  </p>
                </td>
                <td className="collected-content">{checkedBox} </td>
                <td className="disclosed-content">{checkedBox} </td>
              </tr>
              <tr>
                <td className="category-content">
                  <p>
                    <span>
                      Biometric data for the purpose of uniquely identifying an
                      individual
                    </span>
                  </p>
                </td>
                <td className="collected-content">{checkedBox} </td>
                <td className="disclosed-content">{checkedBox} </td>
                {isResponsive ? privacyPolicySensitiveInfo() : null}
              </tr>
            </tbody>
          </table>
        </div>

        <p>
          <strong>
            <span>
              <br />
              <u>We do not collect</u>:
            </span>
          </strong>
          <span>
            &nbsp;other sensitive personal information under the applicable
            state laws.
          </span>
        </p>

        <p>
          <span>
            The categories identified as collected in the tables above were
            collected from the following categories of sources:
          </span>
        </p>

        <ul>
          <li>
            <span>You directly</span>
          </li>
          <li>
            <span>Consumer data resellers</span>
          </li>
          <li>
            <span>Marketing partners</span>
          </li>
          <li>
            <span>Data analytics providers</span>
          </li>
          <li>
            <span>Operating systems and platforms</span>
          </li>
          <li>
            <span>Social networks</span>
          </li>
        </ul>

        <p className="centered-header">
          What We Sell to Third Parties or Share with Third Parties for
          Cross-Context Behavioral Advertising and Right to Opt-Out
        </p>

        <p>
          <span>
            We have sold or shared with third parties for cross-context
            behavioral advertising (<em>e.g.</em>, targeted advertising)
            personal information to third parties in the preceding 12 months as
            disclosed in the table below. We do not sell or share your sensitive
            personal information.
          </span>
        </p>

        <table>
          <tbody>
            <tr>
              <th>
                <p>
                  <strong>
                    <span>
                      Categories sold to or shared with Non-Service Providers
                      over the last 12 months
                    </span>
                  </strong>
                </p>
              </th>
              <th>
                <p>
                  <strong>
                    <span>
                      Categories of Non-Service Providers to whom this category
                      of personal information has been sold or shared
                    </span>
                  </strong>
                </p>
              </th>
            </tr>
            <tr>
              <td>
                <p>
                  <span>Identifiers</span>
                </p>
              </td>
              <td>
                <div>
                  <ul>
                    <li>
                      <span>Analytics providers</span>
                    </li>
                    <li>
                      <span>Advertising partners</span>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <span>Internet or Other Similar Network Activity</span>
                </p>
              </td>
              <td>
                <div>
                  <ul>
                    <li>
                      <span>Analytics providers</span>
                    </li>
                    <li>
                      <span>Advertising partners</span>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <p>
                  <span>Geolocation Data</span>
                </p>
              </td>
              <td>
                <div>
                  <ul>
                    <li>
                      <span>Analytics providers</span>
                    </li>
                    <li>
                      <span>Advertising partners</span>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <p>
          <strong>
            <u>
              <span>We do not sell or share</span>
            </u>
          </strong>
          <strong>
            <span>:</span>
          </strong>
          <span>
            &nbsp;other categories of personal information mentioned in the
            charts above.
          </span>
        </p>

        <p>
          <span>
            The purpose of the notice of Right to Opt-Out is to inform you that
            you have the right to direct us to stop selling or sharing your
            personal information, which is called the &quot;Right to
            Opt-Out.&quot;
          </span>
        </p>

        <p>
          <span>
            You may exercise your Right to Opt-Out by: (1) clicking &quot;Do Not
            Sell or Share My Personal Information&quot; to use the interactive
            tool, or (2) submitting your request through an opt-out preference
            signal/universal opt-out mechanism (described further below in the
            section titled &quot;Opt-Out Preference Signals
          </span>
          /<span>Universal Opt-Out Mechanism &quot;).</span>
        </p>

        <p>
          <span>
            Once you make an opt-out request, we will comply within 15 business
            days.
          </span>
        </p>

        <p>
          <span>
            We do not have actual knowledge that we sell the personal
            information of minors under 16 years of age.
          </span>
        </p>

        <p>
          <span>
            The rights afforded under this State Privacy Disclosure apply solely
            to state privacy laws applicable to us.
          </span>
        </p>

        <p className="centered-header">Your Right to Know</p>

        <p>
          <span>
            Certain states provide the right to request that we disclose any of
            the following:
          </span>
        </p>
        <ul>
          <li>
            <span>
              the categories of personal information we have collected about you
            </span>
          </li>
          <li>
            <span>
              the categories of sources from which the personal information is
              collected
            </span>
          </li>
          <li>
            <span>
              the business or commercial purpose for collecting, selling, or
              sharing with third parties for cross context behavioral
              advertising (e.g., targeted advertising)
            </span>
          </li>
          <li>
            <span>
              the categories of third parties to whom we disclose personal
              information
            </span>
          </li>
          <li>
            <span>
              the specific pieces of personal information we have collected
              about you
            </span>
          </li>
        </ul>

        <p>
          <span>
            You can do this through a verified consumer request. That process is
            described below in the section, &quot;How to Exercise Your
            Rights.&quot;
          </span>
        </p>

        <p className="centered-header">
          Your Right to Request Correction of Inaccurate Personal Information
        </p>

        <p>
          <span>
            Certain states provide the right to request correction of inaccurate
            personal information maintained by us. We may request documentation
            from you to determine the accuracy of the information. If you
            provide us documentation either upon our request or through your own
            initiative, that documentation will only be used for the purpose of
            correcting your personal information and complying with our
            recordkeeping requirements.
          </span>
        </p>

        <p>
          <span>
            As an alternative to correction, we may delete the inaccurate
            information if it does not negatively impact you or if you consent
            to this deletion. We reserve the right to deny this request if
            allowed under applicable law, or if we determine that the contested
            information is more likely than not accurate, based on the totality
            of circumstances. You can submit a correction request through a
            verified consumer request. That process is described below in the
            section, &quot; How to Exercise Your Rights.&quot;
          </span>
        </p>
        <p className="centered-header">
          Your Right to Request Deletion of Your Personal Information
        </p>

        <p>
          <span>
            Certain states provide the right to request that we delete any of
            your personal information collected by us, subject to certain
            exceptions. You can do this through a verified consumer request.
            That process is described below in the section, &quot;How to
            Exercise Your Rights.&quot;
          </span>
        </p>

        <p>
          <span>
            There may be scenarios where we deny your deletion request. If that
            occurs, we will provide you with an explanation as to why we could
            not delete all or some of your personal information.
          </span>
        </p>

        <p className="centered-header">How to Exercise Your Rights</p>

        <p>
          <span>
            You may exercise your rights, as applicable by state law, by
            submitting a verified consumer request to know information (and to
            receive your personal information in a portable format), to correct
            information, or for deletion.
          </span>
        </p>

        <p>
          <span>
            The response to a request to know will provide the personal
            information we have collected and maintained about you, in a
            portable format, unless an exception under applicable state law
            applies. Please note that, depending on the applicable state law, we
            may not be required to provide personal information to you if you
            make multiple requests in a certain time period. We cannot respond
            to your request or provide you with personal information if we
            cannot verify your identity or authority to make the request. We
            will only use personal information provided in a verifiable consumer
            request to verify the requestor&rsquo;s identity or authority to
            make the request.
          </span>
        </p>

        <p>
          <span>You can submit requests using the following&nbsp;</span>
          {webFormLink}
          <span>
            &nbsp;or by calling toll-free 877-353-6053. Your request will be
            verified using information you provide as described on the&nbsp;
          </span>
          {webFormLink}
          <span>
            . If you call 877-353-6053 you will be asked to provide the same
            information requested on the web form for verification purposes.
          </span>
          <span>
            If you submit a request that is not through one of the above
            designated methods or is deficient in some manner unrelated to
            verification, we will provide you with information on how to submit
            the request or remedy any deficiencies with your request.
          </span>
        </p>

        <p>
          <span>
            Once we receive your verifiable consumer request, we will confirm
            receipt of the request within 10 business days, or as allowed under
            applicable law, describing our verification process. We will respond
            to your request within 45 calendar days, or as allowed under
            applicable law, if we are able to verify your identity. Requests for
            deletion will require a separate confirmation that you want your
            information deleted.
          </span>
        </p>

        <p>
          <span>
            Requests to Know or Delete for Child Under the Age of 13: We do not
            knowingly collect information of minors under the age of 13.
          </span>
        </p>

        <p className="centered-header">
          Opt-Out Preference Signals/Universal Opt-Out Mechanism
        </p>

        <p>
          <span>
            Our use of tracking technologies may be considered a sale/sharing
            for targeted advertising under applicable state law. You can opt-out
            of being tracked by these third parties by clicking the &quot;Do Not
            Sell of Share My Personal Information&quot; link at the bottom of
            our website and selecting your preferences, or by broadcasting an
            Opt-Out Preference Signal/Universal Opt-Out Mechanism, such as the
            Global Privacy Control (GPC) (on the browsers and/or browser
            extensions that support such a signal). Your request to opt-out of
            sale/sharing will be linked to your browser identifier only and not
            linked to any account information because the connection between
            your browser and the account is not known to us. Because the signal
            is browser-specific, you will need to broadcast subsequent signals
            if you are accessing our website through different online devices
            (e.g., computers, smartphones, tablets).
          </span>
        </p>

        <p className="centered-header">
          Your Right to Non-Discrimination for the Exercise of a Privacy Right
        </p>

        <p>
          <span>
            We will not discriminate against you for exercising any of your
            state privacy rights. However, we may still offer you certain
            financial incentives permitted under applicable state law, such as
            discounts, rewards, premium features, loyalty accounts, or club card
            programs that can result in different prices, rates, or quality
            levels.
          </span>
        </p>

        <p className="centered-header">
          Using an Authorized Agent to Submit a Request
        </p>

        <p>
          <span>
            <u>California</u>: Only you, or a natural person or a business
            entity registered with the Secretary of State to conduct business in
            California that you have authorized to act on your behalf, may make
            a verifiable consumer request related to your personal information,
            to opt-out of sale or sharing, or to limit the use or disclosure of
            your sensitive personal information. If you use an authorized agent,
            you may provide a power of attorney executed pursuant to California
            Probate Code sections 4000 to 4465. If a power of attorney that
            meets those provisions is not submitted, you will be required to
            provide the authorized agent signed permission to submit a request,
            verify your identity directly by submitting a verified consumer
            request according to the procedures in the section &quot;How to
            Exercise Your Rights,&quot; and directly confirm with us that you
            provided the authorized agent permission to submit the request. To
            submit authorized agent documentation, contact us at{' '}
            <a href="mailto:CustomerAdvocacy@claytonhomes.com">
              customeradvocacy@claytonhomes.com
            </a>
            .
          </span>
        </p>

        <p>
          <span>
            <u>Other States</u>: If you use an authorized agent, you may provide
            a power of attorney executed pursuant to applicable state law. If a
            power of attorney is not submitted, you may be required to provide
            commercially reasonable identification of your identity and evidence
            of your authorized agent’s authority to act on your behalf. For
            example, you may need to provide the authorized agent signed
            permission to submit a request, verify your identity directly by
            submitting a verified consumer request according to the procedures
            in the section &quot;How to Exercise Your Rights,&quot; and directly
            confirm with us that you provided the authorized agent permission to
            submit the request, by contacting us at{' '}
            <a href="mailto:CustomerAdvocacy@claytonhomes.com">
              customeradvocacy@claytonhomes.com
            </a>
            .
          </span>
        </p>

        <div>
          <p className="box-header">Supplemental U.S. State Privacy Rights</p>
        </div>

        <p>
          <strong>
            <span>
              In addition to the privacy rights listed above, certain states
              provide supplemental privacy rights to residents of those states.
            </span>
          </strong>
        </p>

        <p className="centered-header">Right to Appeal</p>

        <p>
          <span>
            Certain states provide you the right to appeal our refusal to take
            action on, or respond to, a verified consumer request. Upon receipt
            of our denial or refusal to take action on, or to respond to, a
            verified consumer request, a consumer has 90 days to submit a
            request to appeal our decision by using the {webFormLink} or by
            calling toll-free 877-353-6053. For those states, we will timely
            inform you in writing of any action taken or not taken in response
            of your request. We will also include a written explanation of the
            reasons for our decision.
          </span>
        </p>

        <p className="centered-header">Right to Opt-Out of Certain Profiling</p>

        <p>
          <span>
            You have the right to opt-out of our use of profiling when used to
            make decisions that produce a legal or similarly significant effect
            concerning you or your interactions with us. Currently, Company does
            not engage in such profiling.
          </span>
        </p>

        <div className="divider-line" />

        <p className="centered-header">Changes to Our Privacy Notice</p>

        <p>
          <span>
            We reserve the right to amend this privacy notice at our discretion
            and at any time. When we make changes to this privacy notice, we
            will post the updated notice on the website and update the
            notice&rsquo;s effective date. Your continued use of our website
            following the posting of changes constitutes your acceptance of such
            changes.
          </span>
        </p>

        <p className="centered-header">Contact for More Information</p>

        <p>
          <span>
            If you have any questions or comments about this notice, the ways in
            which Clayton collects and uses your information, your choices and
            rights regarding such use, or wish to exercise your rights under
            applicable state law, please do not hesitate to contact us at:
          </span>
        </p>

        <p>
          <span>Phone: 877-353-6053</span>
        </p>

        <p>
          <span>Website:&nbsp;</span>
          {webFormLink}
        </p>
      </div>
    </MultiStatePrivacyStyles>
  );
};

const table1Col3Heading = () => {
  return (
    <p>
      <strong>
        Sold to Third Parties or Shared with Third Parties for Cross-Context
        Behavioral Advertising{': '}
      </strong>
    </p>
  );
};

const noticeCollectionGeneralInfo = () => {
  return (
    <>
      <td className="table-1-col-2-content" rowSpan="9">
        <div>
          <ul>
            <li>
              <span>
                To fulfill or meet the reason for which the information is
                provided
              </span>
            </li>
            <li>
              <span>For our internal operational purposes</span>
            </li>
            <li>
              <span>
                For auditing relating to consumer transactions including ad
                impressions and compliance with regulations
              </span>
            </li>
            <li>
              <span>Fraud and security detection</span>
            </li>
            <li>
              <span>Debugging to identify and repair errors</span>
            </li>
            <li>
              <span>
                Short-term data use for the current interaction that is not used
                to build a profile
              </span>
            </li>
            <li>
              <span>
                Servicing transactions and accounts (e.g., customer service,
                maintaining and servicing accounts, customer verification,
                payment processing) and providing our products and any
                additional services
              </span>
            </li>
            <li>
              <span>
                To evaluate or conduct a merger, divestiture, restructuring,
                reorganization, dissolution, or other sale or transfer of some
                or all of our assets, whether as a going concern or as part of a
                bankruptcy, liquidation, or similar proceeding, in which
                personal information held by us is among the assets transferred.
              </span>
            </li>
            <li>
              <span>
                Undertaking internal research for technological development and
                demonstration
              </span>
            </li>
            <li>
              <span>
                Undertaking activities to verify or maintain the quality or
                safety of a service or device that is owned, manufactured,
                manufactured for, or controlled by us, and to improve, upgrade,
                or enhance the service or device that is owned, manufactured,
                manufactured for, or controlled by us
              </span>
            </li>
            <li>
              <span>
                To share the personal information with service providers to
                carry out other business purposes.
              </span>
            </li>
          </ul>
        </div>
      </td>
    </>
  );
};

const table2Col3Heading = () => {
  return (
    <p>
      <strong>
        Sold to or Shared with Third Parties for Cross-Context Behavioral
        Advertising{': '}
      </strong>
    </p>
  );
};

const noticeCollectionSensitiveInfo = () => {
  return (
    <>
      <td rowSpan="5">
        <div>
          <ul>
            <li>
              <span>
                To fulfill or meet the reason for which the information is
                provided
              </span>
            </li>
            <li>
              <span>For our internal operation purposes</span>
            </li>
            <li>
              <span>
                For auditing relating to consumer transactions including
                compliance with regulations
              </span>
            </li>
            <li>
              <span>
                Fraud and security detection, investigation, and resistance
              </span>
            </li>
            <li>
              <span>Debugging to identify and repair errors</span>
            </li>
            <li>
              <span>
                Short-term data use for the current interaction that is not used
                to build a profile
              </span>
            </li>
            <li>
              <span>
                Servicing transactions and accounts (e.g., customer service,
                maintaining and servicing accounts, customer verification,
                payment processing) and providing services on behalf of business
                or service provider (e.g., financing, advertising or marketing,
                analytics)
              </span>
            </li>
            <li>
              <span>
                To evaluate or conduct a merger, divestiture, restructuring,
                reorganization, dissolution, or other sale or transfer of some
                or all of our assets, whether as a going concern or as part of a
                bankruptcy, liquidation, or similar proceeding, in which
                personal information held by us is among the assets transferred.
              </span>
            </li>
            <li>
              <span>
                Undertaking internal research for technological development and
                demonstration
              </span>
            </li>
            <li>
              <span>
                Undertaking activities to verify or maintain the quality or
                safety of a service or device that is owned, manufactured,
                manufactured for, or controlled by us, and to improve, upgrade,
                or enhance the service or device that is owned, manufactured,
                manufactured for, or controlled by us.
              </span>
            </li>
            <li>
              <span>
                To share the personal information with service providers to
                carry out other business purposes.
              </span>
            </li>
          </ul>
        </div>
      </td>
    </>
  );
};

// Contains two lists which are the last two columns in the table.
const privacyPolicyGeneralInfo = () => {
  return (
    <>
      <td className="disclosure-content" rowSpan="9">
        <div>
          <ul>
            <li>
              <span>
                To fulfill or meet the reason for which the information is
                provided
              </span>
            </li>
            <li>
              <span>For our internal operation purposes</span>
            </li>
            <li>
              <span>
                For auditing relating to consumer transactions including ad
                impressions and compliance with regulations
              </span>
            </li>
            <li>
              <span>
                Fraud and security detection, investigation, and resistance
              </span>
            </li>
            <li>
              <span>Debugging to identify and repair errors</span>
            </li>
            <li>
              <span>
                Short-term data use for the current interaction that is not used
                to build a profile
              </span>
            </li>
            <li>
              <span>
                Servicing transactions and accounts (e.g., customer service,
                maintaining and servicing accounts, customer verification,
                payment processing) and providing services on behalf of business
                or service provider (e.g., financing, advertising or marketing,
                analytics)
              </span>
            </li>
            <li>
              <span>
                To evaluate or conduct a merger, divestiture, restructuring,
                reorganization, dissolution, or other sale or transfer of some
                or all of our assets, whether as a going concern or as part of a
                bankruptcy, liquidation, or similar proceeding, in which
                personal information held by us is among the assets transferred.
              </span>
            </li>
            <li>
              <span>
                Undertaking internal research for technological development and
                demonstration
              </span>
            </li>
            <li>
              <span>
                Undertaking activities to verify or maintain the quality or
                safety of a service or device that is owned, manufactured,
                manufactured for, or controlled by us, and to improve, upgrade,
                or enhance the service or device that is owned, manufactured,
                manufactured for, or controlled by us.
              </span>
            </li>
            <li>
              <span>
                To share the personal information with service providers to
                carry out other business purposes.
              </span>
            </li>
          </ul>
        </div>
      </td>
      <td className="third-party-content" rowSpan="9">
        <div>
          <ul>
            <li>
              <span>Cloud storage providers</span>
            </li>
            <li>
              <span>Payment processors</span>
            </li>
            <li>
              <span>Web analytics and advertising providers</span>
            </li>
            <li>
              <span>Web hosting service providers</span>
            </li>
            <li>
              <span>E-mail distribution service providers</span>
            </li>
            <li>
              <span>Help Desk service providers</span>
            </li>
            <li>
              <span>Financial and accounting service providers</span>
            </li>
            <li>
              <span>Cybersecurity service providers</span>
            </li>
            <li>
              <span>Logistics and planning tool providers</span>
            </li>
            <li>
              <span>Customer relationship management tool providers</span>
            </li>
            <li>
              <span>Property preservation and inspection services</span>
            </li>
            <li>
              <span>Home construction contractors</span>
            </li>
            <li>
              <span>Home repair providers</span>
            </li>
            <li>
              <span>Home warranty service providers</span>
            </li>
            <li>
              <span>Customer Survey Solutions</span>
            </li>
            <li>
              <span>Identity & Authentication Service Providers</span>
            </li>
          </ul>
        </div>
      </td>
    </>
  );
};

const privacyPolicySensitiveInfo = () => {
  return (
    <>
      <td className="disclosure-content" rowSpan="5">
        <div>
          <ul>
            <li>
              <span>
                To fulfill or meet the reason for which the information is
                provided
              </span>
            </li>
            <li>
              <span>For our internal operation purposes</span>
            </li>
            <li>
              <span>
                For auditing relating to consumer transactions including
                compliance with regulations
              </span>
            </li>
            <li>
              <span>
                Fraud and security detection, investigation, and resistance
              </span>
            </li>
            <li>
              <span>Debugging to identify and repair errors</span>
            </li>
            <li>
              <span>
                Short-term data use for the current interaction that is not used
                to build a profile
              </span>
            </li>
            <li>
              <span>
                Servicing transactions and accounts (e.g., customer service,
                maintaining and servicing accounts, customer verification,
                payment processing) and providing services on behalf of business
                or service provider (e.g., financing, advertising or marketing,
                analytics)
              </span>
            </li>
            <li>
              <span>
                To evaluate or conduct a merger, divestiture, restructuring,
                reorganization, dissolution, or other sale or transfer of some
                or all of our assets, whether as a going concern or as part of a
                bankruptcy, liquidation, or similar proceeding, in which
                personal information held by us is among the assets transferred.
              </span>
            </li>
            <li>
              <span>
                Undertaking internal research for technological development and
                demonstration
              </span>
            </li>
            <li>
              <span>
                Undertaking activities to verify or maintain the quality or
                safety of a service or device that is owned, manufactured,
                manufactured for, or controlled by us, and to improve, upgrade,
                or enhance the service or device that is owned, manufactured,
                manufactured for, or controlled by us.
              </span>
            </li>
            <li>
              <span>
                To share the personal information with service providers to
                carry out other business purposes.
              </span>
            </li>
          </ul>
        </div>
      </td>
      <td className="third-party-content" rowSpan="5">
        <div>
          <ul>
            <li>
              <span>Cloud storage providers</span>
            </li>
            <li>
              <span>Payment processors</span>
            </li>
            <li>
              <span>Web analytics providers</span>
            </li>
            <li>
              <span>Web hosting service providers</span>
            </li>
            <li>
              <span>E-mail distribution service providers</span>
            </li>
            <li>
              <span>Help Desk service providers</span>
            </li>
            <li>
              <span>Financial and accounting service providers</span>
            </li>
            <li>
              <span>Cybersecurity service providers</span>
            </li>
            <li>
              <span>Logistics and planning tool providers</span>
            </li>
            <li>
              <span>Customer relationship management tool providers</span>
            </li>
            <li>
              <span>Property preservation and inspection services</span>
            </li>
            <li>
              <span>Home construction contractors</span>
            </li>
            <li>
              <span>Home repair providers</span>
            </li>
            <li>
              <span>Home warranty service providers</span>
            </li>
            <li>
              <span>Customer Survey Solutions</span>
            </li>
            <li>
              <span>Identity & Authentication Service Providers</span>
            </li>
          </ul>
        </div>
      </td>
    </>
  );
};

export default MultiStatePrivacy;
